<script setup>
  import { useStore } from 'vuex';
  import { onMounted, onUnmounted, ref, computed, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { collection, query, where, getDocs, getDoc, doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
  import { db } from '@/firebase';
  import { timeSimplified, utcToLocal } from '@/tools/TimeFunctions';

  import DefaultLayout from '@/layouts/Layout.vue';
  import UnitCard from '@/components/cards/UnitCard.vue';
  import GoogleMap from '@/components/maps/GoogleMap.vue';
  import CreateDeviceClassModal from '@/components/modals/CreateDeviceClassModal.vue';
  import CreateEntity from '@/components/modals/CreateEntity-new.vue';
  import UpdateDevice from '@/components/modals/updateDevice.vue';
  import LoadingAnimation3 from '@/components/loadingIcons/LoadingAnimation3.vue';
  import Class from '@/api/class';
  import Device from '@/api/device';
  import { withNormalizedClassIcon } from '@/tools/classIconUrl';
  import { hasPermission } from '@/tools/PermissionChecker';
  import {
    unitCloudAppValues,
    defaultUnitFallbackPath,
  } from '@/tools/cloudAppUnit';
  import {
    resolveDashboardMode,
    alertSeverityForUnit,
    flattenTimeseriesStyleSpecs,
    normalizeQuickMetricSpecs,
  } from '@/tools/dashboardUtils';

  const searchQuery = ref('');
  const allUnits = ref(new Map());
  const isMobileMapView = ref(false);

  let swipeTouchStartX = 0;
  let swipeTouchStartY = 0;
  let swipeLocked = null; // 'h' | 'v' | null
  const swipeDragOffset = ref(0);
  const swipeActive = ref(false);
  const slideContainerRef = ref(null);

  const swipeTrackStyle = computed(() => {
    const base = isMobileMapView.value ? -50 : 0;
    if (swipeActive.value) {
      return { width: '200%', transform: `translateX(calc(${base}% + ${swipeDragOffset.value}px))`, transition: 'none' };
    }
    return { width: '200%', transform: `translateX(${base}%)`, transition: 'transform 300ms ease-in-out' };
  });

  function onSwipeTouchStart(e) {
    swipeTouchStartX = e.touches[0].clientX;
    swipeTouchStartY = e.touches[0].clientY;
    swipeLocked = null;
    swipeDragOffset.value = 0;
  }

  function onSwipeTouchMove(e) {
    const dx = e.touches[0].clientX - swipeTouchStartX;
    const dy = e.touches[0].clientY - swipeTouchStartY;

    if (swipeLocked === null) {
      if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
      swipeLocked = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v';
    }
    if (swipeLocked === 'v') return;

    e.preventDefault();
    swipeActive.value = true;
    // Resist dragging past the edge
    swipeDragOffset.value = isMobileMapView.value ? Math.max(0, dx) : Math.min(0, dx);
  }

  function onSwipeTouchEnd() {
    if (!swipeActive.value) { swipeLocked = null; return; }
    swipeActive.value = false;
    swipeLocked = null;
    const threshold = Math.min(80, window.innerWidth * 0.25);
    const dx = swipeDragOffset.value;
    if (!isMobileMapView.value && dx < -threshold) isMobileMapView.value = true;
    else if (isMobileMapView.value && dx > threshold) isMobileMapView.value = false;
    swipeDragOffset.value = 0;
  }
  const dashboardMode = ref('view');
  const filtersOpen = ref(false);

  // Device classes
  const deviceClasses = ref([]);
  const unitClassMap = ref({});
  const showCreateClassModal = ref(false);
  const classSelectResetKey = ref(0);
  const createClassDetails = ref(null);

  // Loading states
  const pageLoading = ref(true);
  const classesLoading = ref(false);
  const assigningUnitId = ref(null);
  const assigningEntityUnitId = ref(null);
  const showCreateEntityModal = ref(false);
  const createEntityModalDeviceIds = ref([]);
  const showUpdateUnitModal = ref(false);
  const updatingUnitId = ref(null);
  const submitting = ref(false);
  const submitError = ref('');
  const showStreamFallbackNotice = ref(false);
  const isManualRefreshInProgress = ref(false);
  const manualRefreshError = ref('');

  const canEditDashboard = computed(() => hasPermission('editDashboard'));
  const canEditSubEntityLinks = computed(() => hasPermission('editUnit'));
  const canCreateEntityFromCard = computed(() => hasPermission('createEntity'));

  const route = useRoute();
  const router = useRouter();
  async function syncDashboardModeQuery(mode) {
    const nextMode = resolveDashboardMode(mode);
    const currentMode = resolveDashboardMode(route.query.mode);
    if (nextMode === currentMode) return;

    const nextQuery = { ...route.query };
    if (nextMode === 'edit') {
      nextQuery.mode = 'edit';
    } else {
      delete nextQuery.mode;
    }

    await router.replace({
      path: route.path,
      query: nextQuery,
    });
  }

  async function setDashboardMode(mode) {
    if (!canEditDashboard.value && mode === 'edit') return;
    dashboardMode.value = resolveDashboardMode(mode);
    await syncDashboardModeQuery(dashboardMode.value);
  }

  watch(() => route.query.mode, (rawMode) => {
    const nextMode = resolveDashboardMode(rawMode);
    if (nextMode === 'edit' && !canEditDashboard.value) {
      dashboardMode.value = 'view';
      syncDashboardModeQuery('view');
      return;
    }
    dashboardMode.value = nextMode;
  }, { immediate: true });

  const store = useStore();
  const timeFormat = store.state.user.timeFormat;
  const entityData = store.state.user.entityData || {};
  const subEntities = store.state.user.entityData[store.state.user.entity]?.subEntities || [];
  const entityId = ref(null);

  // --- Multi-filter state ---
  // Arrays of active filter values - empty = all shown
  const statusFilters = ref([]);
  const alertSeverityFilters = ref([]);
  const classFilters = ref([]);
  const subEntityFilters = ref([]);
  const cloudAppFilters = ref([]);

  function toggleStatusFilter(value) {
    const idx = statusFilters.value.indexOf(value);
    if (idx === -1) statusFilters.value.push(value);
    else statusFilters.value.splice(idx, 1);
  }

  function toggleClassFilter(value) {
    const idx = classFilters.value.indexOf(value);
    if (idx === -1) classFilters.value.push(value);
    else classFilters.value.splice(idx, 1);
  }

  function toggleSubEntityFilter(value) {
    const idx = subEntityFilters.value.indexOf(value);
    if (idx === -1) subEntityFilters.value.push(value);
    else subEntityFilters.value.splice(idx, 1);
  }

  function toggleCloudAppFilter(value) {
    const idx = cloudAppFilters.value.indexOf(value);
    if (idx === -1) cloudAppFilters.value.push(value);
    else cloudAppFilters.value.splice(idx, 1);
  }

  function clearAllFilters() {
    statusFilters.value = [];
    alertSeverityFilters.value = [];
    classFilters.value = [];
    subEntityFilters.value = [];
    cloudAppFilters.value = [];
  }

  const activeFilterCount = computed(() =>
    statusFilters.value.length + alertSeverityFilters.value.length + classFilters.value.length + subEntityFilters.value.length + cloudAppFilters.value.length
  );

  // --- Computed stats for the header strip ---
  const allUnitsArray = computed(() => Array.from(allUnits.value.entries()));

  const stats = computed(() => {
    let online = 0, offline = 0, inactive = 0, undeployed = 0;
    for (const [, value] of allUnits.value) {
      if (!value.active) { inactive++; continue; }
      if (!value.lastActivityUTC || value.lastActivityUTC === '') { undeployed++; continue; }
      const t = timeSimplified(utcToLocal(value.lastActivityUTC, timeFormat));
      if (t === 'Current') online++;
      else offline++;
    }
    return { total: allUnits.value.size, online, offline, inactive, undeployed };
  });

  // Sub-entity options derived from loaded units
  const subEntityOptions = computed(() => {
    const seen = new Set();
    for (const [, value] of allUnits.value) {
      for (const id of (value.entities || [])) {
        if (subEntities.includes(id)) seen.add(id);
      }
    }
    return [...seen].map(id => ({ id, name: entityData[id]?.name || id }));
  });

  // Helper: resolve a unit's status bucket
  function unitStatusBucket(value) {
    if (!value.active) return 'inactive';
    if (!value.lastActivityUTC || value.lastActivityUTC === '') return 'undeployed';
    return timeSimplified(utcToLocal(value.lastActivityUTC, timeFormat)) === 'Current' ? 'online' : 'offline';
  }

  const cloudAppOptions = computed(() => {
    const counts = new Map();
    for (const [, value] of allUnits.value) {
      const appValues = unitCloudAppValues(value);
      for (const app of appValues) {
        counts.set(app, (counts.get(app) || 0) + 1);
      }
    }

    return [...counts.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([key, count]) => ({ key, label: key, count }));
  });

  const unitAlertSeverityMap = computed(() => {
    const map = {};
    for (const [id, data] of allUnitsArray.value) {
      map[id] = alertSeverityForUnit(data);
    }
    return map;
  });

  // --- Filtered + searched list ---
  const displayedUnits = computed(() => {
    const result = [];
    const sq = searchQuery.value.trim().toLowerCase();

    for (const [key, value] of allUnits.value) {
      // Status filter
      if (statusFilters.value.length > 0 && !statusFilters.value.includes(unitStatusBucket(value))) continue;

      // Class filter
      if (classFilters.value.length > 0) {
        const classId = unitClassMap.value[key] ?? null;
        const bucket = classId ? classId : 'unclassified';
        if (!classFilters.value.includes(bucket)) continue;
      }

      // Sub-entity filter
      if (subEntityFilters.value.length > 0) {
        const unitEntities = value.entities || [];
        const hasMatch = subEntityFilters.value.some(id => unitEntities.includes(id));
        if (!hasMatch) continue;
      }

      // Cloud app filter
      if (cloudAppFilters.value.length > 0) {
        const appValues = unitCloudAppValues(value);
        const matchesApp = appValues.some((app) => cloudAppFilters.value.includes(app));
        if (!matchesApp) continue;
      }

      // Alert severity filter
      if (alertSeverityFilters.value.length > 0) {
        const severity = unitAlertSeverityMap.value[key];
        if (!severity || !alertSeverityFilters.value.includes(severity)) continue;
      }

      // Search
      if (sq) {
        const nameMatch = value.friendlyName?.toLowerCase().includes(sq);
        const idMatch = key.toLowerCase().includes(sq);
        if (!nameMatch && !idMatch) continue;
      }

      // Sort online units to top
      if (unitStatusBucket(value) === 'online') result.unshift([key, value]);
      else result.push([key, value]);
    }
    return result;
  });

  const unitsForModal = computed(() =>
    allUnitsArray.value.map(([id, data]) => ({ dataID: id, friendlyName: data.friendlyName || id }))
  );

  const classQuickMetricSpecs = computed(() => {
    const map = {};
    for (const cls of deviceClasses.value) {
      if (!cls?.id) continue;

      const rawSpecs =
        cls.quickMetrics ||
        cls.quickMetricSpecs ||
        cls.dashboardQuickMetrics ||
        cls.dashboard?.quickMetrics ||
        cls.dashboard?.metrics ||
        flattenTimeseriesStyleSpecs(cls.quickMetricSpecifications || cls.specifications);

      map[cls.id] = normalizeQuickMetricSpecs(rawSpecs);
    }
    return map;
  });

  function unitQuickMetricSpecs(dataID) {
    const classId = unitClassMap.value[dataID] ?? null;
    if (!classId) return [];
    return classQuickMetricSpecs.value[classId] || [];
  }

  const unitQuickMetricSpecsMap = computed(() => {
    const map = {};
    for (const [id] of allUnitsArray.value) {
      map[id] = unitQuickMetricSpecs(id);
    }
    return map;
  });

  const selectedUnitForEdit = computed(() => {
    if (!updatingUnitId.value) return null;
    return allUnits.value.get(updatingUnitId.value) || null;
  });

  const selectedUnitClassName = computed(() => {
    if (!updatingUnitId.value) return '';
    const classId = unitClassMap.value[updatingUnitId.value] ?? null;
    if (!classId) return '';
    return deviceClasses.value.find((c) => c.id === classId)?.name || '';
  });

  function openUpdateUnitModal(dataID) {
    if (!canEditSubEntityLinks.value) return;
    if (!dataID || !allUnits.value.has(dataID)) return;
    updatingUnitId.value = dataID;
    showUpdateUnitModal.value = true;
  }

  function closeUpdateUnitModal() {
    showUpdateUnitModal.value = false;
    updatingUnitId.value = null;
  }

  async function updateUnitFromDashboard(payload) {
    if (!updatingUnitId.value) return;
    const dataID = updatingUnitId.value;
    const formData = new FormData();
    formData.append('dataID', dataID);
    formData.append('friendlyName', payload?.friendlyName ?? '');
    formData.append('description', payload?.description ?? '');
    formData.append('notes', payload?.notes ?? '');
    formData.append('quickMetrics', JSON.stringify(payload?.quickMetrics || []));
    if (payload?.image) formData.append('image', payload.image);

    try {
      const response = await Device.put('', formData);
      if (response?.status !== 'success') {
        alert('Failed to update device');
        closeUpdateUnitModal();
        return;
      }

      const previous = allUnits.value.get(dataID) || {};
      const next = new Map(allUnits.value);
      next.set(dataID, {
        ...previous,
        ...(response?.details || {}),
        friendlyName: payload?.friendlyName ?? previous.friendlyName,
        description: payload?.description ?? previous.description,
        notes: payload?.notes ?? previous.notes,
        quickMetrics: Array.isArray(payload?.quickMetrics)
          ? payload.quickMetrics
          : previous.quickMetrics,
      });
      allUnits.value = next;
      closeUpdateUnitModal();
    } catch (error) {
      console.error('Error updating unit from dashboard:', error);
      alert('Failed to update device');
      closeUpdateUnitModal();
    }
  }

  const statusOptions = [
    { key: 'online',      label: 'Online',       dot: 'bg-green-500' },
    { key: 'offline',     label: 'Offline',      dot: 'bg-mc-steel/80' },
    { key: 'inactive',    label: 'Deactivated',  dot: 'bg-mc-steel/40' },
    { key: 'undeployed',  label: 'Undeployed',   dot: 'bg-mc-steel/30' },
  ];

  function applyQuickStatusFilter(value) {
    alertSeverityFilters.value = [];
    if (value === 'total') {
      statusFilters.value = [];
      return;
    }
    statusFilters.value = [value];
  }

  function applyQuickAlertFilter(value) {
    statusFilters.value = [];
    if (alertSeverityFilters.value.length === 1 && alertSeverityFilters.value[0] === value) {
      alertSeverityFilters.value = [];
      return;
    }
    alertSeverityFilters.value = [value];
  }

  const alertSummary = computed(() => {
    let critical = 0;
    let warning = 0;
    for (const [id] of allUnitsArray.value) {
      const severity = unitAlertSeverityMap.value[id];
      if (severity === 'critical') critical += 1;
      if (severity === 'warning') warning += 1;
    }
    return { critical, warning };
  });

  function onSummaryBarWheel(event) {
    const container = event.currentTarget;
    if (!(container instanceof HTMLElement)) return;
    if (container.scrollWidth <= container.clientWidth) return;

    // Convert regular vertical wheel movement into horizontal scrolling.
    const horizontalDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
      ? event.deltaX
      : event.deltaY;

    if (horizontalDelta === 0) return;
    container.scrollLeft += horizontalDelta;
    event.preventDefault();
  }

  let deviceStream = null;
  let fallbackPollInFlight = false;
  let suppressStreamRemovals = false;
  let isComponentActive = true;

  function mergeDevicesIntoState(devices) {
    if (!Array.isArray(devices)) return;
    const next = new Map(allUnits.value);
    for (const device of devices) {
      const { id, ...rest } = device || {};
      if (!id) continue;
      next.set(id, { ...next.get(id), ...rest });
    }
    allUnits.value = next;
  }

  function getEntityScopeIds() {
    const viewing = store.state.user.entityViewing;
    if (Array.isArray(viewing) && viewing.length > 0) {
      return [...new Set(viewing.filter(Boolean))];
    }
    return store.state.user.entity ? [store.state.user.entity] : [];
  }

  async function pollDevicesSnapshot() {
    const scopeEntityIds = getEntityScopeIds();
    if (scopeEntityIds.length === 0 || fallbackPollInFlight) return false;
    fallbackPollInFlight = true;
    try {
      const responses = await Promise.allSettled(
        scopeEntityIds.map((id) => Device.get(`/all?entity=${encodeURIComponent(id)}`))
      );

      const aggregated = new Map();
      let successCount = 0;

      for (const result of responses) {
        if (result.status !== 'fulfilled') continue;
        const response = result.value;
        if (response?.status !== 'success' || !Array.isArray(response.details)) continue;
        successCount += 1;
        for (const device of response.details) {
          const { id, ...rest } = device || {};
          if (!id) continue;
          aggregated.set(id, { ...aggregated.get(id), ...rest });
        }
      }

      if (aggregated.size === 0) return false;

      const aggregatedList = Array.from(aggregated.entries()).map(([id, data]) => ({ id, ...data }));
      // Never replace from fallback snapshots; merge-only avoids temporary
      // scope/API partials from collapsing the current list.
      if (successCount !== scopeEntityIds.length) {
        console.warn('Fallback snapshot is partial; merging without replacement.');
      }
      mergeDevicesIntoState(aggregatedList);
      return true;
    } catch (error) {
      console.error('Device snapshot poll failed:', error);
      return false;
    } finally {
      fallbackPollInFlight = false;
    }
  }

  async function refreshFallbackData() {
    if (isManualRefreshInProgress.value) return;
    isManualRefreshInProgress.value = true;
    manualRefreshError.value = '';
    try {
      const loaded = await pollDevicesSnapshot();
      if (!loaded) {
        manualRefreshError.value = 'Refresh returned no data. Please try again.';
      }
    } catch (error) {
      console.error('Manual device refresh failed:', error);
      manualRefreshError.value = 'Refresh failed. Please try again.';
    } finally {
      isManualRefreshInProgress.value = false;
    }
  }

  onMounted(async () => {
    pageLoading.value = true;
    try {
      const scopeEntityIds = getEntityScopeIds();
      entityId.value = scopeEntityIds[0] ?? null;

      if (scopeEntityIds.length === 0) {
        console.error('No valid entity found for user');
        pageLoading.value = false;
        return;
      }

      // Both the stream's first batch and the classes API must resolve before we show anything.
      let streamReady = false;
      let classesReady = false;
      const tryUnlock = () => {
        if (streamReady && classesReady) pageLoading.value = false;
      };

      const nextDeviceStream = await Device.stream();
      if (!isComponentActive) {
        nextDeviceStream.close();
        return;
      }
      deviceStream = nextDeviceStream;

      const handleUpsert = (event) => {
        try {
          const devices = JSON.parse(event.data);
          if (!Array.isArray(devices)) return;
          showStreamFallbackNotice.value = false;
          manualRefreshError.value = '';
          suppressStreamRemovals = false;
          mergeDevicesIntoState(devices);
          if (!streamReady) { streamReady = true; tryUnlock(); }
        } catch (e) {
          console.error('Error parsing device stream event:', e);
        }
      };

      const handleRemoved = (event) => {
        try {
          if (suppressStreamRemovals) return;
          const devices = JSON.parse(event.data);
          if (!Array.isArray(devices)) return;
          const next = new Map(allUnits.value);
          for (const device of devices) {
            if (device.id) next.delete(device.id);
          }
          allUnits.value = next;
          if (!streamReady) { streamReady = true; tryUnlock(); }
        } catch (e) {
          console.error('Error parsing device stream event:', e);
        }
      };

      deviceStream.addEventListener('added', handleUpsert);
      deviceStream.addEventListener('modified', handleUpsert);
      deviceStream.addEventListener('removed', handleRemoved);
      deviceStream.addEventListener('open', () => {
        showStreamFallbackNotice.value = false;
        manualRefreshError.value = '';
        suppressStreamRemovals = false;
      });

      deviceStream.addEventListener('error', (err) => {
        console.error('Device stream error:', err);
        suppressStreamRemovals = true;
        showStreamFallbackNotice.value = true;
        // Unblock the UI even if the stream fails
        if (!streamReady) { streamReady = true; tryUnlock(); }
      });

      // Load an initial snapshot so users behind strict networks
      // still see data while SSE reconnects.
      const initialSnapshotLoaded = await pollDevicesSnapshot();
      if (initialSnapshotLoaded && !streamReady) {
        streamReady = true;
        tryUnlock();
      }

      await loadDeviceClasses();
      classesReady = true;
      tryUnlock();
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      pageLoading.value = false;
    }
  });

  onMounted(() => {
    slideContainerRef.value?.addEventListener('touchmove', onSwipeTouchMove, { passive: false });
  });

  onUnmounted(() => {
    isComponentActive = false;
    slideContainerRef.value?.removeEventListener('touchmove', onSwipeTouchMove);
    if (deviceStream) {
      deviceStream.close();
      deviceStream = null;
    }
  });

  async function loadDeviceClasses() {
    if (!entityId.value) return;
    classesLoading.value = true;
    try {
      const response = await Class.get();
      if (response.status === 200) {
        const classesData = await response.json();
        const loaded = Array.isArray(classesData)
          ? classesData
          : (classesData?.classes ?? classesData?.details ?? []);
        deviceClasses.value = loaded.map(withNormalizedClassIcon);
        const map = {};
        for (const cls of loaded) {
          for (const id of (cls.unitIds ?? cls.devices ?? [])) {
            map[id] = cls.id;
          }
        }
        unitClassMap.value = map;
      } else {
        deviceClasses.value = [];
        unitClassMap.value = {};
      }
    } finally {
      classesLoading.value = false;
    }
  }

  async function toggleUnitSubEntity(dataID, subEntityId) {
    if (!dataID || !subEntityId) return;
    const unit = allUnits.value.get(dataID);
    if (!unit) return;
    const prevEntities = [...(unit.entities || [])];
    const isLinked = prevEntities.includes(subEntityId);

    const protectedIds = new Set();
    const ev = store.state.user.entityViewing;
    const baseEntity = store.state.user.entity;
    if (baseEntity) protectedIds.add(baseEntity);
    if (Array.isArray(ev)) ev.forEach((id) => protectedIds.add(id));
    if (isLinked && protectedIds.has(subEntityId)) return;

    assigningEntityUnitId.value = dataID;
    const nextEntities = isLinked
      ? prevEntities.filter((id) => id !== subEntityId)
      : [...new Set([...prevEntities, subEntityId])];
    const nextMap = new Map(allUnits.value);
    nextMap.set(dataID, { ...unit, entities: nextEntities });
    allUnits.value = nextMap;

    try {
      const dataRef = doc(db, 'data', dataID);
      if (isLinked) {
        await updateDoc(dataRef, { entities: arrayRemove(subEntityId) });
      } else {
        await updateDoc(dataRef, { entities: arrayUnion(subEntityId) });
      }
    } catch (err) {
      console.error('Error updating unit entity links:', err);
      const revert = new Map(allUnits.value);
      revert.set(dataID, { ...unit, entities: prevEntities });
      allUnits.value = revert;
    } finally {
      assigningEntityUnitId.value = null;
    }
  }

  function openCreateEntityModal(dataID = null) {
    createEntityModalDeviceIds.value = dataID ? [dataID] : [];
    showCreateEntityModal.value = true;
  }

  function closeCreateEntityModal() {
    showCreateEntityModal.value = false;
    createEntityModalDeviceIds.value = [];
  }

  async function onEntityCreated({ entityId, deviceIds }) {
    if (!entityId || !deviceIds?.length) return;
    const next = new Map(allUnits.value);
    for (const id of deviceIds) {
      try {
        await updateDoc(doc(db, 'data', id), { entities: arrayUnion(entityId) });
      } catch (e) {
        console.error('Error linking unit to new entity:', e);
      }
      const u = next.get(id);
      if (u) {
        const ents = [...(u.entities || [])];
        if (!ents.includes(entityId)) ents.push(entityId);
        next.set(id, { ...u, entities: ents });
      }
    }
    allUnits.value = next;
  }

  async function assignUnitToClass(dataID, classId) {
    if (!dataID) return;
    const previousClassId = unitClassMap.value[dataID] ?? null;
    assigningUnitId.value = dataID;

    if (classId) {
      unitClassMap.value = { ...unitClassMap.value, [dataID]: classId };
    } else {
      const updated = { ...unitClassMap.value };
      delete updated[dataID];
      unitClassMap.value = updated;
    }

    try {
      if (previousClassId && previousClassId !== classId) {
        const oldClass = deviceClasses.value.find((c) => c.id === previousClassId);
        if (oldClass) {
          const oldMembers = (oldClass.unitIds ?? oldClass.devices ?? []).filter((id) => id !== dataID);
          const oldBody = {
            name: oldClass.name,
            description: oldClass.description || '',
            devices: oldMembers,
            quickMetrics: oldClass.quickMetrics ?? oldClass.quickMetricSpecs ?? [],
          };
          const oldIcon = oldClass.icon || oldClass.iconUrl;
          if (oldIcon) oldBody.icon = oldIcon;
          await Class.put(oldBody, previousClassId);
        }
      }
      if (classId) {
        const newClass = deviceClasses.value.find((c) => c.id === classId);
        if (newClass) {
          const existingMembers = newClass.unitIds ?? newClass.devices ?? [];
          if (!existingMembers.includes(dataID)) {
            const newBody = {
              name: newClass.name,
              description: newClass.description || '',
              devices: [...existingMembers, dataID],
              quickMetrics: newClass.quickMetrics ?? newClass.quickMetricSpecs ?? [],
            };
            const newIcon = newClass.icon || newClass.iconUrl;
            if (newIcon) newBody.icon = newIcon;
            await Class.put(newBody, classId);
          }
        }
      }
      await loadDeviceClasses();
    } catch (err) {
      console.error('Error assigning unit to class:', err);
      await loadDeviceClasses();
    } finally {
      assigningUnitId.value = null;
    }
  }

  function openCreateClassModal(dataID = null) {
    submitError.value = '';
    let quickMetricSeed = [];
    if (dataID) {
      const unit = allUnits.value.get(dataID) || null;
      if (Array.isArray(unit?.quickMetrics) && unit.quickMetrics.length > 0) {
        quickMetricSeed = unit.quickMetrics;
      } else if (Array.isArray(unit?.quickMetricSpecs) && unit.quickMetricSpecs.length > 0) {
        quickMetricSeed = unit.quickMetricSpecs;
      } else if (unit?.snapshots && typeof unit.snapshots === 'object' && !Array.isArray(unit.snapshots)) {
        // Dotted snapshot keys (source.field) are normalized by the class modal.
        quickMetricSeed = unit.snapshots;
      }
    }
    createClassDetails.value = {
      name: '',
      description: '',
      unitIds: dataID ? [dataID] : [],
      quickMetrics: quickMetricSeed,
    };
    showCreateClassModal.value = true;
  }

  function closeCreateClassModal() {
    showCreateClassModal.value = false;
    createClassDetails.value = null;
    submitError.value = '';
    classSelectResetKey.value += 1;
  }

  async function handleCreate(payload) {
    const { name: payloadName, description: payloadDescription, unitIds = [], icon, quickMetrics = [] } = payload || {};
    const payloadForApi = {
      name: payloadName,
      description: payloadDescription || '',
      devices: [...unitIds],
      quickMetrics: quickMetrics,
    };
    if (icon) {
      payloadForApi.icon = icon;
    }
    const response = await Class.post(payloadForApi);
    if (response.status === 200) {
      await loadDeviceClasses();
      closeCreateClassModal();
    } else {
      const msg = typeof response.error === 'string' ? response.error : 'Could not create class.';
      submitError.value = msg;
      console.error('Error creating class:', response.error);
    }
  }

  async function handleSubmit(payload) {
    submitting.value = true;
    submitError.value = '';
    try {
      await handleCreate(payload);
    } catch (e) {
      console.error('Device class save failed:', e);
      submitError.value = 'Request failed. Check your connection and try again.';
    } finally {
      submitting.value = false;
    }
  }

  const toUnitDashboard = async (dataID) => {
    if (!dataID || dataID === 'null' || dataID === 'undefined') return;
    try {
      const dataDoc = await getDocs(query(collection(db, "data"), where("__name__", "==", dataID)));
      let unitData = null;
      if (!dataDoc.empty) {
        unitData = dataDoc.docs[0].data();
        store.dispatch('setDevice', { ...unitData, dataID });
        if (unitData.defaultTemplate) {
          const templateDoc = await getDoc(doc(db, "templates", unitData.defaultTemplate));
          if (templateDoc.exists()) {
            const template = templateDoc.data();
            template.id = templateDoc.id;
            template.dataID = dataID;
            store.dispatch('setTemplate', template);
            if (template.pages?.length > 0) {
              const firstValidPage = [...template.pages]
                .sort((a, b) => a.order - b.order)
                .find(page => page.id && page.id !== 'null' && page.id !== 'undefined');
              if (firstValidPage) {
                router.push(`/dashboard/${dataID}/p/${firstValidPage.id}`);
                return;
              }
            }
          }
        }
      }
      router.push(defaultUnitFallbackPath(dataID, unitData));
    } catch (error) {
      console.error('Error navigating to unit dashboard:', error);
      router.push(`/dashboard/${dataID}/management`);
    }
  };
</script>

<template>
  <DefaultLayout title="Dashboard">
    <div
      ref="slideContainerRef"
      class="flex flex-col md:flex-row flex-1 min-h-0 min-w-0 overflow-hidden w-full"
      @touchstart.passive="onSwipeTouchStart"
      @touchend.passive="onSwipeTouchEnd"
    >

      <!-- Mobile: segmented toggle - List vs Map (replaces single swap button) -->
      <div class="md:hidden w-full flex justify-center px-3 pt-1 pb-2 shrink-0">
        <div
          class="inline-flex w-full max-w-sm rounded-mc-md bg-mc-steel/50 dark:bg-mc-charcoal/90 p-1 shadow-inner"
          role="group"
          aria-label="Dashboard view"
        >
          <button
            type="button"
            @click="isMobileMapView = false"
            class="flex-1 min-h-[44px] rounded-mc-sm text-sm font-semibold font-body transition-all duration-160 touch-manipulation focus:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-mc-charcoal"
            :class="!isMobileMapView
              ? 'bg-mc-white dark:bg-mc-charcoal text-mc-blue shadow-sm'
              : 'text-ink-muted dark:text-on-dark-muted active:bg-mc-steel/50 dark:active:bg-mc-steel/30'"
          >
            List
          </button>
          <button
            type="button"
            @click="isMobileMapView = true"
            class="flex-1 min-h-[44px] rounded-mc-sm text-sm font-semibold font-body transition-all duration-160 touch-manipulation focus:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-mc-charcoal"
            :class="isMobileMapView
              ? 'bg-mc-white dark:bg-mc-charcoal text-mc-blue shadow-sm'
              : 'text-ink-muted dark:text-on-dark-muted active:bg-mc-steel/50 dark:active:bg-mc-steel/30'"
          >
            Map
          </button>
        </div>
      </div>

      <!-- Sliding panels (mobile) / side-by-side (desktop) -->
      <div class="flex-1 min-h-0 overflow-hidden md:contents">
        <div
          class="flex h-full md:contents touch-pan-y"
          :style="swipeTrackStyle"
        >

      <!-- List Panel -->
      <div
        class="w-1/2 flex flex-col flex-none min-h-0 min-w-0 overflow-hidden border-r border-mc-steel dark:border-line-on-dark bg-mesh-surface-card md:w-[650px]"
      >
        <!-- Summary + Status Shortcuts -->
        <div
          class="flex-shrink-0 overflow-x-auto overscroll-x-contain border-b border-mc-steel dark:border-line-on-dark [-webkit-overflow-scrolling:touch] px-2 py-2"
          @wheel="onSummaryBarWheel"
        >
          <div class="flex gap-1.5 min-w-max">
            <button
              type="button"
              :aria-pressed="statusFilters.length === 0 && alertSeverityFilters.length === 0"
              @click="applyQuickStatusFilter('total')"
              class="flex-1 min-w-[72px] min-h-[56px] flex flex-col items-center justify-between px-2.5 pt-2 pb-2 bg-mesh-surface-card border rounded-mc-sm transition-all duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
              :class="statusFilters.length === 0 && alertSeverityFilters.length === 0 ? 'border-mc-blue bg-mc-blue/15 dark:bg-mc-blue/20' : 'border-mc-steel/30 dark:border-line-on-dark hover:border-mc-steel/60'"
              title="Show all units"
            >
              <div class="w-7 h-[2px] rounded-full mx-auto bg-mc-blue" aria-hidden="true"></div>
              <div>
                <p class="font-display font-black text-[1.3rem] leading-none text-mesh-text-primary">{{ stats.total }}</p>
                <p class="font-mono text-[0.52rem] uppercase tracking-[0.12em] text-ink-muted dark:text-on-dark-muted mt-1">Total</p>
              </div>
            </button>

            <button
              type="button"
              :aria-pressed="statusFilters.length === 1 && statusFilters[0] === 'online'"
              @click="applyQuickStatusFilter('online')"
              class="flex-1 min-w-[72px] min-h-[56px] flex flex-col items-center justify-between px-2.5 pt-2 pb-2 bg-mesh-surface-card border rounded-mc-sm transition-all duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
              :class="statusFilters.length === 1 && statusFilters[0] === 'online' ? 'border-green-500 bg-green-500/15 dark:bg-green-500/20' : 'border-mc-steel/30 dark:border-line-on-dark hover:border-mc-steel/60'"
              title="Filter online units"
            >
              <div class="w-7 h-[2px] rounded-full mx-auto bg-green-500" aria-hidden="true"></div>
              <div>
                <p class="font-display font-black text-[1.3rem] leading-none text-mesh-text-primary">{{ stats.online }}</p>
                <p class="font-mono text-[0.52rem] uppercase tracking-[0.12em] text-ink-muted dark:text-on-dark-muted mt-1">Online</p>
              </div>
            </button>

            <button
              type="button"
              :aria-pressed="statusFilters.length === 1 && statusFilters[0] === 'offline'"
              @click="applyQuickStatusFilter('offline')"
              class="flex-1 min-w-[72px] min-h-[56px] flex flex-col items-center justify-between px-2.5 pt-2 pb-2 bg-mesh-surface-card border rounded-mc-sm transition-all duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
              :class="statusFilters.length === 1 && statusFilters[0] === 'offline' ? 'border-mc-steel bg-mc-steel/20 dark:bg-mc-steel/10' : 'border-mc-steel/30 dark:border-line-on-dark hover:border-mc-steel/60'"
              title="Filter offline units"
            >
              <div class="w-7 h-[2px] rounded-full mx-auto bg-mc-steel/80" aria-hidden="true"></div>
              <div>
                <p class="font-display font-black text-[1.3rem] leading-none text-mesh-text-primary">{{ stats.offline }}</p>
                <p class="font-mono text-[0.52rem] uppercase tracking-[0.12em] text-ink-muted dark:text-on-dark-muted mt-1">Offline</p>
              </div>
            </button>

            <button
              type="button"
              :aria-pressed="alertSeverityFilters.length === 1 && alertSeverityFilters[0] === 'critical'"
              @click="applyQuickAlertFilter('critical')"
              class="flex-1 min-w-[72px] min-h-[56px] flex flex-col items-center justify-between px-2.5 pt-2 pb-2 bg-mesh-surface-card border rounded-mc-sm transition-all duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
              :class="alertSeverityFilters.length === 1 && alertSeverityFilters[0] === 'critical' ? 'border-mc-red bg-mc-red/15 dark:bg-mc-red/20' : 'border-mc-steel/30 dark:border-line-on-dark hover:border-mc-steel/60'"
              title="Filter critical alerts"
            >
              <div class="w-7 h-[2px] rounded-full mx-auto bg-mc-red" aria-hidden="true"></div>
              <div>
                <p class="font-display font-black text-[1.3rem] leading-none text-mesh-text-primary">{{ alertSummary.critical }}</p>
                <p class="font-mono text-[0.52rem] uppercase tracking-[0.12em] text-ink-muted dark:text-on-dark-muted mt-1">Critical</p>
              </div>
            </button>

            <button
              type="button"
              :aria-pressed="alertSeverityFilters.length === 1 && alertSeverityFilters[0] === 'warning'"
              @click="applyQuickAlertFilter('warning')"
              class="flex-1 min-w-[72px] min-h-[56px] flex flex-col items-center justify-between px-2.5 pt-2 pb-2 bg-mesh-surface-card border rounded-mc-sm transition-all duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
              :class="alertSeverityFilters.length === 1 && alertSeverityFilters[0] === 'warning' ? 'border-amber-500 bg-amber-500/15 dark:bg-amber-500/20' : 'border-mc-steel/30 dark:border-line-on-dark hover:border-mc-steel/60'"
              title="Filter warning alerts"
            >
              <div class="w-7 h-[2px] rounded-full mx-auto bg-amber-500" aria-hidden="true"></div>
              <div>
                <p class="font-display font-black text-[1.3rem] leading-none text-mesh-text-primary">{{ alertSummary.warning }}</p>
                <p class="font-mono text-[0.52rem] uppercase tracking-[0.12em] text-ink-muted dark:text-on-dark-muted mt-1">Warning</p>
              </div>
            </button>

            <button
              type="button"
              :aria-pressed="statusFilters.length === 1 && statusFilters[0] === 'inactive'"
              @click="applyQuickStatusFilter('inactive')"
              class="flex-1 min-w-[72px] min-h-[56px] flex flex-col items-center justify-between px-2.5 pt-2 pb-2 bg-mesh-surface-card border rounded-mc-sm transition-all duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
              :class="statusFilters.length === 1 && statusFilters[0] === 'inactive' ? 'border-mc-steel bg-mc-steel/20 dark:bg-mc-steel/10' : 'border-mc-steel/30 dark:border-line-on-dark hover:border-mc-steel/60'"
              title="Filter deactivated units"
            >
              <div class="w-7 h-[2px] rounded-full mx-auto bg-mc-steel/40" aria-hidden="true"></div>
              <div>
                <p class="font-display font-black text-[1.3rem] leading-none text-mesh-text-primary">{{ stats.inactive }}</p>
                <p class="font-mono text-[0.52rem] uppercase tracking-[0.12em] text-ink-muted dark:text-on-dark-muted mt-1">Deactivated</p>
              </div>
            </button>

            <button
              type="button"
              :aria-pressed="statusFilters.length === 1 && statusFilters[0] === 'undeployed'"
              @click="applyQuickStatusFilter('undeployed')"
              class="flex-1 min-w-[72px] min-h-[56px] flex flex-col items-center justify-between px-2.5 pt-2 pb-2 bg-mesh-surface-card border rounded-mc-sm transition-all duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
              :class="statusFilters.length === 1 && statusFilters[0] === 'undeployed' ? 'border-mc-steel bg-mc-steel/20 dark:bg-mc-steel/10' : 'border-mc-steel/30 dark:border-line-on-dark hover:border-mc-steel/60'"
              title="Filter undeployed units"
            >
              <div class="w-7 h-[2px] rounded-full mx-auto bg-mc-steel/30" aria-hidden="true"></div>
              <div>
                <p class="font-display font-black text-[1.3rem] leading-none text-mesh-text-primary">{{ stats.undeployed }}</p>
                <p class="font-mono text-[0.52rem] uppercase tracking-[0.12em] text-ink-muted dark:text-on-dark-muted mt-1">Undeployed</p>
              </div>
            </button>
          </div>
        </div>

        <!-- Search + Filter Toolbar -->
        <div class="flex flex-col border-b border-mc-steel dark:border-line-on-dark flex-shrink-0 bg-mesh-surface-card">
          <div class="px-3 pt-2 pb-1 flex items-center justify-between gap-2">
            <p class="text-[10px] uppercase tracking-[0.12em] font-mono text-ink-muted dark:text-on-dark-muted">Operate mode</p>
            <div class="inline-flex rounded-mc-sm bg-mc-cloud dark:bg-mc-charcoal p-1" role="group" aria-label="Dashboard mode">
              <button
                type="button"
                class="px-3 py-1 text-sm font-medium font-body rounded-[8px] transition-colors duration-160"
                :class="dashboardMode === 'view'
                  ? 'bg-mc-white dark:bg-mc-ink text-mc-blue shadow-sm'
                  : 'text-ink-muted dark:text-on-dark-muted hover:bg-mc-white/60 dark:hover:bg-mc-ink/30'"
                @click="setDashboardMode('view')"
              >
                View
              </button>
              <button
                type="button"
                class="px-3 py-1 text-sm font-medium font-body rounded-[8px] transition-colors duration-160 disabled:opacity-50 disabled:cursor-not-allowed"
                :class="dashboardMode === 'edit'
                  ? 'bg-mc-white dark:bg-mc-ink text-mc-blue shadow-sm'
                  : 'text-ink-muted dark:text-on-dark-muted hover:bg-mc-white/60 dark:hover:bg-mc-ink/30'"
                :disabled="!canEditDashboard"
                :title="canEditDashboard ? 'Enable dashboard edit controls' : 'You do not have permission to edit dashboard layout'"
                @click="setDashboardMode('edit')"
              >
                Edit Layout
              </button>
            </div>
          </div>

          <div
            v-if="showStreamFallbackNotice"
            class="mx-3 mt-2 mb-1 flex flex-wrap items-start sm:items-center gap-2 rounded-mc-sm border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700/70 dark:bg-amber-900/30 dark:text-amber-100"
          >
            <p class="flex-1 min-w-[240px]">
              Live updates are temporarily unavailable on this device. Data will not auto-refresh while the stream is blocked.
              Use Refresh Data to manually fetch updates, or ask IT to allow
              <span class="font-semibold">api.meshcommengineering.com/api/v1/stream/devices</span>.
            </p>
            <button
              type="button"
              class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium font-body rounded-mc-sm border border-mc-steel dark:border-line-on-dark bg-mc-white dark:bg-mc-charcoal text-mc-ink dark:text-on-dark hover:bg-mc-cloud dark:hover:bg-mc-charcoal transition-colors duration-160 shadow-mc-sm shrink-0 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="isManualRefreshInProgress"
              @click="refreshFallbackData"
              title="Refresh devices data from API"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992V4.356" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M2.977 14.652H7.97v4.992" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M3.51 9a9 9 0 0114.13-3.36L21.015 9" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M20.49 15a9 9 0 01-14.13 3.36L2.985 15" />
              </svg>
              <span class="hidden sm:inline">{{ isManualRefreshInProgress ? 'Refreshing...' : 'Refresh Data' }}</span>
              <span class="sm:hidden">Refresh</span>
            </button>
          </div>
          <p v-if="showStreamFallbackNotice && manualRefreshError" class="mx-3 mb-1 text-xs text-red-500 dark:text-red-400">{{ manualRefreshError }}</p>

          <!-- Search row -->
          <div class="flex items-center gap-2 px-3 pt-2 pb-2">
            <input
              type="search"
              enterkeyhint="search"
              autocomplete="off"
              class="flex-grow min-w-0 min-h-[44px] px-3 text-base md:text-sm font-body rounded-mc-sm border border-mc-steel dark:border-line-on-dark bg-mc-white dark:bg-mc-charcoal text-mc-ink dark:text-on-dark placeholder:text-ink-muted dark:placeholder:text-on-dark-muted transition-colors duration-160 focus:outline-none focus-visible:border-mc-blue focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
              v-model="searchQuery"
              placeholder="Search by name or ID..."
            />
            <!-- Filter toggle button -->
            <button
              type="button"
              @click="filtersOpen = !filtersOpen"
              class="flex-shrink-0 flex items-center justify-center gap-1.5 min-h-[44px] px-3 rounded-pill text-xs font-semibold font-body transition-colors duration-160 border touch-manipulation"
              :class="filtersOpen || activeFilterCount > 0
                ? 'bg-mc-blue text-mc-white border-mc-blue shadow-mc-sm'
                : 'bg-mc-cloud dark:bg-mc-charcoal text-ink-muted dark:text-on-dark-muted border-mc-steel dark:border-line-on-dark hover:bg-mc-steel/50 dark:hover:bg-mc-steel/30'"
            >
              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4h18M7 8h10M11 12h2M11 16h2"/>
              </svg>
              Filters
              <span
                v-if="activeFilterCount > 0"
                class="rounded-full w-4 h-4 flex items-center justify-center text-[10px] leading-none font-bold"
                :class="filtersOpen || activeFilterCount > 0 ? 'bg-mc-white/25 text-mc-white' : 'bg-mc-blue text-mc-white'"
              >{{ activeFilterCount }}</span>
            </button>
          </div>

          <!-- Device count -->
          <p v-if="!pageLoading" class="px-3 pb-2 text-[11px] font-mono text-ink-muted dark:text-on-dark-muted">
            Showing {{ displayedUnits.length }} devices
          </p>

          <!-- Expandable filter panel -->
          <div v-if="filtersOpen" class="px-3 pb-3 flex flex-col gap-3 border-t border-mc-steel/50 dark:border-line-on-dark pt-2">

            <!-- Status -->
            <div>
              <p class="text-[10px] uppercase tracking-[0.14em] font-mono font-medium text-ink-muted dark:text-on-dark-muted mb-1.5">Status</p>
              <div class="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  v-for="opt in statusOptions"
                  :key="opt.key"
                  @click="toggleStatusFilter(opt.key)"
                  class="flex items-center gap-1.5 min-h-[40px] sm:min-h-0 px-2.5 py-2 sm:py-1 rounded-full text-xs font-medium transition-colors border touch-manipulation"
                  :class="statusFilters.includes(opt.key)
                    ? 'bg-mc-blue text-mc-white border-mc-blue'
                    : 'bg-mc-white dark:bg-mc-charcoal text-ink-muted dark:text-on-dark-muted border-mc-steel dark:border-line-on-dark hover:border-ink-muted dark:hover:border-line-on-dark'"
                >
                  <span class="w-2 h-2 rounded-full flex-shrink-0" :class="opt.dot" />
                  {{ opt.label }}
                  <span class="text-[10px] opacity-60">({{ stats[opt.key] }})</span>
                </button>
              </div>
            </div>

            <!-- Class (only shown if classes exist) -->
            <div v-if="deviceClasses.length > 0">
              <p class="text-[10px] uppercase tracking-[0.14em] font-mono font-medium text-ink-muted dark:text-on-dark-muted mb-1.5">Class</p>
              <div class="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  v-for="cls in deviceClasses"
                  :key="cls.id"
                  @click="toggleClassFilter(cls.id)"
                  class="min-h-[40px] sm:min-h-0 px-2.5 py-2 sm:py-1 rounded-full text-xs font-medium transition-colors border touch-manipulation"
                  :class="classFilters.includes(cls.id)
                    ? 'bg-mc-blue text-mc-white border-mc-blue'
                    : 'bg-mc-white dark:bg-mc-charcoal text-ink-muted dark:text-on-dark-muted border-mc-steel dark:border-line-on-dark hover:border-ink-muted dark:hover:border-line-on-dark'"
                >
                  {{ cls.name }}
                </button>
                <button
                  type="button"
                  @click="toggleClassFilter('unclassified')"
                  class="min-h-[40px] sm:min-h-0 px-2.5 py-2 sm:py-1 rounded-full text-xs font-medium transition-colors border touch-manipulation"
                  :class="classFilters.includes('unclassified')
                    ? 'bg-mc-blue text-mc-white border-mc-blue'
                    : 'bg-mc-white dark:bg-mc-charcoal text-ink-muted dark:text-on-dark-muted border-mc-steel dark:border-line-on-dark hover:border-ink-muted dark:hover:border-line-on-dark'"
                >
                  Unclassified
                </button>
              </div>
            </div>

            <!-- Sub-entity (only shown if sub-entities exist) -->
            <div v-if="subEntityOptions.length > 0">
              <p class="text-[10px] uppercase tracking-[0.14em] font-mono font-medium text-ink-muted dark:text-on-dark-muted mb-1.5">Entity</p>
              <div class="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  v-for="se in subEntityOptions"
                  :key="se.id"
                  @click="toggleSubEntityFilter(se.id)"
                  class="min-h-[40px] sm:min-h-0 px-2.5 py-2 sm:py-1 rounded-full text-xs font-medium transition-colors border touch-manipulation"
                  :class="subEntityFilters.includes(se.id)
                    ? 'bg-mc-blue text-mc-white border-mc-blue'
                    : 'bg-mc-white dark:bg-mc-charcoal text-ink-muted dark:text-on-dark-muted border-mc-steel dark:border-line-on-dark hover:border-ink-muted dark:hover:border-line-on-dark'"
                >
                  {{ se.name }}
                </button>
              </div>
            </div>

            <!-- Cloud app -->
            <div>
              <p class="text-[10px] uppercase tracking-[0.14em] font-mono font-medium text-ink-muted dark:text-on-dark-muted mb-1.5">Cloud App</p>
              <div class="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  v-for="opt in cloudAppOptions"
                  :key="opt.key"
                  @click="toggleCloudAppFilter(opt.key)"
                  class="min-h-[40px] sm:min-h-0 px-2.5 py-2 sm:py-1 rounded-full text-xs font-medium transition-colors border touch-manipulation"
                  :class="cloudAppFilters.includes(opt.key)
                    ? 'bg-mc-blue text-mc-white border-mc-blue'
                    : 'bg-mc-white dark:bg-mc-charcoal text-ink-muted dark:text-on-dark-muted border-mc-steel dark:border-line-on-dark hover:border-ink-muted dark:hover:border-line-on-dark'"
                >
                  {{ opt.label }}
                  <span class="text-[10px] opacity-60">({{ opt.count }})</span>
                </button>
              </div>
            </div>

            <!-- Clear button -->
            <button
              v-if="activeFilterCount > 0"
              type="button"
              @click="clearAllFilters"
              class="self-start min-h-[44px] sm:min-h-0 px-1 text-xs font-body text-mc-red hover:underline touch-manipulation"
            >
              Clear all filters
            </button>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="pageLoading" class="flex-1 min-h-0 flex flex-col items-center justify-center gap-3">
          <LoadingAnimation3 size="60px" />
          <p class="text-sm font-body text-ink-muted dark:text-on-dark-muted">Loading units...</p>
        </div>

        <!-- Unit List -->
        <template v-else>
          <div class="flex flex-1 min-h-0 flex-col overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch] bg-mesh-surface-page touch-pan-y">
            <template v-if="displayedUnits.length > 0">
              <UnitCard
                v-for="([id, data]) in displayedUnits"
                :key="id"
                @click="toUnitDashboard(id)"
                :unitData="data"
                :dataID="id"
                :timeFormat="timeFormat"
                :subEntities="subEntities"
                :entityData="entityData"
                :device-classes="deviceClasses"
                :unit-class-id="unitClassMap[id] || null"
                :alert-severity="unitAlertSeverityMap[id] || null"
                :quick-metric-specs="unitQuickMetricSpecs(id)"
                :select-reset-key="classSelectResetKey"
                :class-assigning="assigningUnitId === id"
                :classes-loading="classesLoading"
                :can-edit-sub-entity-links="canEditSubEntityLinks"
                :entity-link-assigning="assigningEntityUnitId === id"
                :can-create-entity="canCreateEntityFromCard"
                :can-edit-quick-metrics="canEditSubEntityLinks && dashboardMode === 'edit'"
                @assign-to-class="(classId) => assignUnitToClass(id, classId)"
                @create-class="(dataID) => openCreateClassModal(dataID)"
                @toggle-sub-entity="(subId) => toggleUnitSubEntity(id, subId)"
                @create-entity="(dataID) => openCreateEntityModal(dataID)"
                @edit-quick-metrics="(dataID) => openUpdateUnitModal(dataID)"
              />
            </template>
            <div v-else class="flex flex-1 flex-col items-center justify-center gap-2 px-4 py-12 text-ink-muted dark:text-on-dark-muted min-h-[50vh]">
              <svg class="w-10 h-10 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p class="text-sm font-body font-medium text-ink-muted dark:text-on-dark-muted">No units found</p>
              <p v-if="searchQuery" class="text-xs font-body text-ink-muted dark:text-on-dark-muted">Try a different search term</p>
            </div>
          </div>
        </template>
      </div>

      <!-- Map Panel -->
      <div
        class="w-1/2 flex flex-col flex-1 min-h-0 min-w-0 overflow-hidden bg-mesh-surface-card"
      >
        <GoogleMap
          class="flex-1 min-h-0 h-full w-full"
          :unitData="displayedUnits"
          :timeFormat="timeFormat"
          :subEntities="subEntities"
          :entityData="entityData"
          :deviceClasses="deviceClasses"
          :unitClassMap="unitClassMap"
          :alertSeverityMap="unitAlertSeverityMap"
          :quickMetricSpecsMap="unitQuickMetricSpecsMap"
          :markers-ready="!pageLoading"
          @navigate-to-unit="toUnitDashboard"
        />
      </div>

        </div>
      </div>

      <!-- Create Class Modal -->
      <CreateDeviceClassModal
        v-if="showCreateClassModal"
        :units="unitsForModal"
        :units-loading="false"
        :class-details="createClassDetails"
        :submitting="submitting"
        :submit-error="submitError"
        @close="closeCreateClassModal"
        @submit="handleSubmit"
      />

      <CreateEntity
        v-if="showCreateEntityModal"
        :initial-device-ids="createEntityModalDeviceIds"
        @close="closeCreateEntityModal"
        @entity-created="onEntityCreated"
      />

      <UpdateDevice
        v-if="showUpdateUnitModal && selectedUnitForEdit"
        :dataID="updatingUnitId"
        :friendlyName="selectedUnitForEdit.friendlyName || ''"
        :description="selectedUnitForEdit.description || ''"
        :notes="selectedUnitForEdit.notes || ''"
        :quick-metrics="selectedUnitForEdit.quickMetrics || selectedUnitForEdit.quickMetricSpecs || []"
        :snapshots="selectedUnitForEdit.snapshots || {}"
        :class-quick-metrics="unitQuickMetricSpecs(updatingUnitId)"
        :class-name="selectedUnitClassName"
        @close="closeUpdateUnitModal"
        @submitForm="updateUnitFromDashboard"
      />

    </div>
  </DefaultLayout>
</template>
